import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * Generic searchable dropdown component
 * @param {Array} options - Array of option objects with { value, label } or { code, name } structure
 * @param {string} value - Currently selected value
 * @param {Function} onChange - Callback function when selection changes (receives event object)
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional CSS classes
 * @param {boolean} error - Whether to show error styling
 * @param {string} name - Input name attribute
 * @param {Function} getOptionValue - Function to extract value from option (default: checks 'value' or 'code')
 * @param {Function} getOptionLabel - Function to extract label from option (default: checks 'label' or 'name')
 * @param {boolean} allowFreeText - Whether to allow free text entry when value doesn't match any option
 */
const SearchableDropdown = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder, 
  className, 
  error,
  name,
  getOptionValue,
  getOptionLabel,
  allowFreeText = false
}) => {
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const inputContainerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownListRef = useRef(null);

  // Default functions to extract value and label from options
  const getValue = getOptionValue || ((option) => option.value || option.code || option);
  const getLabel = getOptionLabel || ((option) => option.label || option.name || String(option));

  // Find the selected option and set the display text
  useEffect(() => {
    const selectedOption = options.find(option => getValue(option) === value || getLabel(option) === value);
    const newSelectedText = selectedOption ? getLabel(selectedOption) : (value || '');
    setSelectedText(newSelectedText);
    if (!isOpen) {
      setSearchText(newSelectedText);
    }
  }, [value, isOpen, options]);

  const highlightMatchingText = (text, highlight) => {
    if (!highlight || !text) {
      return <span>{text}</span>;
    }

    const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.toString().split(regex);
    return (
      <span>
        {parts.map((part, index) =>
          part.toLowerCase() === highlight.toLowerCase() ? (
            <span key={index} style={{ backgroundColor: '#ffeb3b', fontWeight: 'bold' }}>{part}</span>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchText(newValue);
    setIsOpen(true);
    
    // If free text is allowed, update the value immediately as user types
    if (allowFreeText) {
      onChange({
        target: {
          name: name || 'searchable-dropdown',
          value: newValue,
          type: 'text'
        }
      });
    }
  };

  const handleItemSelect = (option) => {
    const optionValue = getValue(option);
    const optionLabel = getLabel(option);
    setSearchText(optionLabel);
    setSelectedText(optionLabel);
    setIsOpen(false);
    onChange({
      target: {
        name: name || 'searchable-dropdown',
        value: optionValue,
        type: 'text'
      }
    });
  };

  const handleClickOutside = useCallback((e) => {
    // Check if click is outside both the input container and the dropdown list
    const isInsideInput = inputContainerRef.current && inputContainerRef.current.contains(e.target);
    const isInsideDropdown = dropdownListRef.current && dropdownListRef.current.contains(e.target);
    
    // Only close if click is outside both the input and the dropdown
    if (!isInsideInput && !isInsideDropdown) {
      setIsOpen(false);
      
      // If free text is allowed and the current search text doesn't match any option, use it
      if (allowFreeText && searchText.trim()) {
        const matchesOption = options.some(option => {
          const label = getLabel(option);
          return label.toLowerCase() === searchText.toLowerCase();
        });
        
        if (!matchesOption) {
          // Use the typed text as the value
          setSelectedText(searchText);
          onChange({
            target: {
              name: name || 'searchable-dropdown',
              value: searchText,
              type: 'text'
            }
          });
        } else {
          setSearchText(selectedText);
        }
      } else {
        setSearchText(selectedText);
      }
    }
  }, [allowFreeText, searchText, selectedText, options, getLabel, onChange, name]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, handleClickOutside]);

  // Scroll into view when dropdown opens to ensure it's fully visible
  useEffect(() => {
    if (isOpen && inputContainerRef.current) {
      // Use setTimeout to ensure the dropdown has rendered
      setTimeout(() => {
        const inputElement = inputContainerRef.current;
        if (inputElement) {
          // Scroll the input into view, positioning it near the top with enough space for dropdown
          // The dropdown has maxHeight of 300px, so we need at least that much space below
          inputElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
          
          // Additional scroll to ensure dropdown has space (accounting for dropdown max height of 300px + padding)
          setTimeout(() => {
            const rect = inputElement.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const dropdownHeight = 300; // maxHeight of dropdown
            const padding = 20; // extra padding
            
            // If there's not enough space below, scroll down a bit more
            if (rect.bottom + dropdownHeight + padding > viewportHeight) {
              const scrollAmount = (rect.bottom + dropdownHeight + padding) - viewportHeight;
              window.scrollBy({
                top: scrollAmount,
                behavior: 'smooth'
              });
            }
          }, 100);
        }
      }, 50);
    }
  }, [isOpen]);

  // Filter options based on search text
  const filteredOptions = options.filter(option => {
    const label = getLabel(option);
    return !searchText || label.toLowerCase().includes(searchText.toLowerCase());
  });

  return (
    <div style={{ position: 'relative' }} className={className}>
      <div ref={inputContainerRef} style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          name={name || 'searchable-dropdown'}
          value={searchText}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder || 'Select or search...'}
          className={`form-input ${error ? 'error' : ''}`}
          autoComplete="off"
          style={{ paddingRight: '35px' }}
        />
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
            if (!isOpen) {
              setSearchText('');
              inputRef.current?.focus();
            } else {
              setSearchText(selectedText);
            }
          }}
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <span style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            fontSize: '12px'
          }}>▼</span>
        </button>
      </div>

      {isOpen && filteredOptions.length > 0 && (
        <div 
          ref={dropdownListRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxHeight: '300px',
            overflowY: 'auto',
            marginTop: '4px'
          }}
        >
          {filteredOptions.map((option, index) => {
            const optionLabel = getLabel(option);
            const isSelected = optionLabel === selectedText;
            return (
              <div
                key={getValue(option) || index}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleItemSelect(option);
                }}
                style={{
                  padding: '10px 15px',
                  cursor: 'pointer',
                  borderBottom: index < filteredOptions.length - 1 ? '1px solid #eee' : 'none',
                  backgroundColor: isSelected ? '#f0f0f0' : 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected ? '#f0f0f0' : 'white';
                }}
              >
                {highlightMatchingText(optionLabel, searchText)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

SearchableDropdown.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  error: PropTypes.bool,
  name: PropTypes.string,
  getOptionValue: PropTypes.func,
  getOptionLabel: PropTypes.func,
  allowFreeText: PropTypes.bool
};

export default SearchableDropdown;




